import { Store } from "@/pages/aigc/hooks/useContext";
import { memoryStore } from "@/pages/aigc/hooks/useContext/memoryStore";
import { isEqual } from "lodash-es";
import { useEffect, useMemo, useState, useContext, createContext } from "react";
export const store = memoryStore();

export const useLocalState = <T>(key: string, defaultValue: T) => {
  const [value, setValue] = useState<T>(() => store.getItem(key, defaultValue));
  // useMemo(() => {
  //   console.log('useLocalState useMemo', value);
  // }, []);

  const { getItem, subscribe } = store;

  // subscribe to changes on this key, and change the state when they happen
  useEffect(() => {
    const storedValue = getItem(key, defaultValue);
    // 使用函数式更新避免将 value 加入依赖导致无限循环
    setValue((prev) => isEqual(prev, storedValue) ? prev : storedValue);

    const unsubscribe = subscribe(key, (newValue) => {
      setValue(typeof newValue === 'undefined' ? defaultValue : newValue);
    });
    return () => unsubscribe();
  }, [key, subscribe, defaultValue, getItem]);

  // 模拟实现 React useState 的 setValue 功能
  // 支持两种方式设置新值:
  // 1. 直接传入新值
  // 2. 传入一个函数,函数接收前一个值作为参数,返回新的值
  const setItem = (newValue: T | ((prev: T) => T)) => {
    // console.log('setItem', newValue, key, store.getItem(key));
    if (typeof newValue === 'function') {
      // 如果传入的是函数
      // 从 store 中获取当前值作为函数参数
      const pre = store.getItem(key, defaultValue);
      // 将 newValue 转换为更新函数类型
      const updateFn = newValue as (prev: T) => T;
      // 执行更新函数得到新值
      const nextValue = updateFn(pre);
      // 将新值保存到 store 中
      store.setItem(key, nextValue);
      // 更新组件状态
      // setValue(nextValue);
    } else {
      // 如果传入的是具体值
      // 直接保存到 store 中
      store.setItem(key, newValue);
      // 更新组件状态
      // setValue(newValue);
    }
  }; // 空依赖数组,确保 setItem 函数引用稳定

  return [value, setItem] as const;
};

// 模拟实现 React useRef
// useRef 是一个简单的可变容器,不具备 setValue 功能,它只提供了一个 .current 属性用于存取值
// 修改 .current 不会触发组件重新渲染
export const useLocalRef = <T>(key: string, defaultValue: T) => {
  const ref = useMemo(() => {
    if (store.getItem(key)) {
      return store.getItem(key);
    }
    store.setItem(key, {current: defaultValue});
    return store.getItem(key);
  }, [defaultValue, key]);
  // 初始化时,如果 ref.current 为 'complete', 切回去的时候则重置为 null, 猜你想问接口依赖于状态标识来触发接口，其他状态统一会在新增对话时，统一重置
  useEffect(() => ()=>{
    if (ref?.current === 'complete') {
      ref.current = null;
    }
  }, [ref]);
  return ref;
};


// 方便扩展(如果只是想读取useChatHistoryu里面的某个全局状态，但是不想导出这个hook，可以直接在这里读取全局数据，无需导出庞大的业务hook)
// 创建上下文,基于store，额外创建读取key，修改key，监听key的方法，
export const StoreContext = createContext<Store>(store);

// 注入
export const useAgentStoreContext = () => useContext(StoreContext);

// 使用
// const { getItem, setItem, subscribe } = useAgentStoreContext();
// const data= getItem('xxx');
